import {
  BlogPostDto,
  BlogPostDetailDto,
  BlogCategoryDto,
  CreateBlogPostCommand,
  UpdateBlogPostCommand,
} from "../types/app/blogTypes";

export interface UIBlogPost {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  featuredImage: string;
  categoryIds?: string[];
  categoryName: string;
  categoryId: string;
  tags: string[];
  author: {
    name: string;
    avatar: string;
  };
  publishDate: string;
  updatedDate?: string | null;
  readTime: string;
  metaTitle: string;
  metaDescription: string;
  views: number;
  featured: boolean;
  keyInsights: string[];
  status?: "draft" | "published" | "trashed";
}

export const adaptBlogPostFromApi = (
  apiPost: BlogPostDto | BlogPostDetailDto,
  status?: "draft" | "published" | "trashed"
): UIBlogPost => {
  const postStatus = status || "published";

  return {
    id: apiPost.id,
    slug: apiPost.slug,
    title: apiPost.title,
    excerpt: apiPost.excerpt,
    content: "content" in apiPost ? apiPost.content : "",
    featuredImage: apiPost.featuredImage || "",
    categoryIds: apiPost.categoryId ? [apiPost.categoryId] : [],
    categoryName: apiPost.categoryName,
    categoryId: apiPost.categoryId,
    tags: [],
    author: {
      name: apiPost.author || "MamiMarkets Inc.",
      avatar: "",
    },
    publishDate: apiPost.publishedDate,
    updatedDate: apiPost.updatedDate ?? null,
    readTime: `${apiPost.readTime} min read`,
    metaTitle: apiPost.title,
    metaDescription: apiPost.excerpt,
    views: 0,
    featured: false,
    keyInsights: apiPost.keyInsights || [],
    status: postStatus,
  };
};

export const adaptBlogPostToCreateCommand = (
  uiPost: UIBlogPost | any
): CreateBlogPostCommand => {
  // Extract number from "5 min read" format
  const readTimeMatch = uiPost.readTime ? uiPost.readTime.match(/\d+/) : null;
  const readTime = readTimeMatch ? parseInt(readTimeMatch[0]) : 5;

  // Handle category - check updated fields first, then fall back to original
  let categoryId = "";
  // Priority 1: Check 'categories' array (from PostEditor)
  if ((uiPost as any).categories && (uiPost as any).categories.length > 0) {
    categoryId = (uiPost as any).categories[0];
  }
  // Priority 2: Check 'category' string (from PostEditor)
  else if ((uiPost as any).category) {
    categoryId = (uiPost as any).category;
  }
  // Priority 3: Check 'categoryIds' array
  else if (uiPost.categoryIds && uiPost.categoryIds.length > 0) {
    categoryId = uiPost.categoryIds[0];
  }
  // Priority 4: Fall back to 'categoryId' (original from API)
  else if (uiPost.categoryId) {
    categoryId = uiPost.categoryId;
  }

  return {
    title: uiPost.title,
    content: uiPost.content,
    excerpt: uiPost.excerpt,
    featuredImage: uiPost.featuredImage,
    readTime: readTime,
    publishedDate: uiPost.publishDate,
    isPublished: uiPost.status === "published",
    author: uiPost.author.name,
    categoryId: categoryId,
    keyInsights: uiPost.keyInsights || [],
  };
};

export const adaptBlogPostToUpdateCommand = (
  uiPost: UIBlogPost | any
): UpdateBlogPostCommand => {
  const readTimeMatch = uiPost.readTime ? uiPost.readTime.match(/\d+/) : null;
  const readTime = readTimeMatch ? parseInt(readTimeMatch[0]) : 5;

  let categoryId = "";
  if ((uiPost as any).categories && (uiPost as any).categories.length > 0) {
    categoryId = (uiPost as any).categories[0];
  }
  else if ((uiPost as any).category) {
    categoryId = (uiPost as any).category;
  }
  else if (uiPost.categoryIds && uiPost.categoryIds.length > 0) {
    categoryId = uiPost.categoryIds[0];
  }
  else if (uiPost.categoryId) {
    categoryId = uiPost.categoryId;
  }

  const command: any = {
    id: uiPost.id,
    title: uiPost.title,
    excerpt: uiPost.excerpt,
    featuredImage: uiPost.featuredImage,
    readTime: readTime,
    publishedDate: uiPost.publishDate,
    isPublished: uiPost.status === "published",
    author: "MamiMarkets Inc.",
    // author: uiPost.author.name,
    categoryId: categoryId,
    keyInsights: uiPost.keyInsights || [],
  };

  if (uiPost.content && uiPost.content.trim() !== '') {
    command.content = uiPost.content;
  }

  return command;
};

export const adaptBlogCategoryFromApi = (
  apiCategory: BlogCategoryDto
): {
  id: string;
  name: string;
  slug: string;
  description: string;
  postCount: number;
} => {
  return {
    id: apiCategory.id,
    name: apiCategory.name,
    slug: apiCategory.slug,
    description: apiCategory.description,
    postCount: apiCategory.postCount || 0,
  };
};
